import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import {
    useDeleteServiceAccountMutation,
    useLazyGetServiceAccountsQuery,
} from '@fiji/common/src/features/serviceAccount/serviceAccountApi';
import type {
    ServiceAccountList,
    ServiceAccountListItem,
    ServiceAccountListPayload,
} from '@fiji/common/src/types/serviceAccount';
import { useTranslation } from 'react-i18next';

type RevokeOutcome = {
    successCount: number;
    failedIds: string[];
};

type ServiceAccountHandlerOptions = {
    realmPrefix?: string;
    organizationId?: string;
    enabled?: boolean;
    includeRealmPrefixInCreatePath?: boolean;
};

type ServiceAccountHandlerState = RevokeOutcome & {
    serviceAccounts?: ServiceAccountList;
    isLoading: boolean;
    setQuery: Dispatch<SetStateAction<ServiceAccountListPayload | undefined>>;
    selectedIds: string[];
    setSelectedIds: Dispatch<SetStateAction<string[]>>;
    idsToRevoke: string[];
    setIdsToRevoke: Dispatch<SetStateAction<string[]>>;
    filteredAccounts: ServiceAccountListItem[];
    isDeleting: boolean;
    deleteSuccess: boolean;
    isDeleteVisible: boolean;
    createAccount: () => void;
    openDelete: () => void;
    cancelDelete: () => void;
    revoke: () => Promise<RevokeOutcome | undefined>;
};

export const useServiceAccountHandler = ({
    realmPrefix,
    organizationId,
    enabled = true,
    includeRealmPrefixInCreatePath = true,
}: ServiceAccountHandlerOptions): ServiceAccountHandlerState => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const currentRealmName = useTypedSelector((state) => state.common.selectedRealm);
    const [query, setQuery] = useState<ServiceAccountListPayload>();
    const [getServiceAccounts, { data: serviceAccounts, isLoading, isFetching }] = useLazyGetServiceAccountsQuery();
    const [deleteServiceAccount, { isLoading: isDeleting, reset: resetMutation, isSuccess: isDeleteSuccess }] =
        useDeleteServiceAccountMutation();
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [idsToRevoke, setIdsToRevoke] = useState<string[]>([]);
    const [filteredAccounts, setFilteredAccounts] = useState<ServiceAccountListItem[]>([]);
    const [isDeleteVisible, setIsDeleteVisible] = useState(false);
    const [outcome, setOutcome] = useState<RevokeOutcome>();
    const pending = useRef(false);

    useEffect(() => {
        if (enabled && query) {
            void getServiceAccounts({
                payload: query,
                realmPrefix,
                ...(organizationId ? { organizationId } : {}),
            });
        }
    }, [enabled, query, realmPrefix, organizationId, getServiceAccounts]);

    const createAccount = (): void => {
        const path = `/${currentRealmName}/addApiAccount`;
        navigate(includeRealmPrefixInCreatePath && realmPrefix ? `${path}/${realmPrefix}` : path);
    };

    const openDelete = (): void => {
        if (!selectedIds.length || isDeleting || pending.current) return;
        resetMutation();
        setOutcome(undefined);
        setIdsToRevoke(selectedIds);
        setFilteredAccounts(
            serviceAccounts?.records.filter((account) => selectedIds.includes(account.serviceAccountId)) ?? []
        );
        setIsDeleteVisible(true);
    };

    const cancelDelete = (): void => {
        setIsDeleteVisible(false);
    };

    const notifyOutcome = ({ successCount, failedIds }: RevokeOutcome): void => {
        if (failedIds.length) {
            dispatch(
                setMessageContent({
                    isOpen: true,
                    extraArgs: { variant: 'error' },
                    message: t(
                        successCount
                            ? 'API_MANAGEMENT:SERVICE_ACCOUNT.SERVICE_ACCOUNTS_DELETE_PARTIAL_MSG'
                            : 'API_MANAGEMENT:SERVICE_ACCOUNT.SERVICE_ACCOUNTS_DELETE_FAILED_MSG',
                        { replace: { successCount, failedCount: failedIds.length } }
                    ),
                })
            );
            return;
        }
        dispatch(
            setMessageContent({
                isOpen: true,
                message: t('API_MANAGEMENT:SERVICE_ACCOUNT.SERVICE_ACCOUNTS_DELETED_MSG', {
                    replace: { totalSA: successCount },
                }),
            })
        );
    };

    const revoke = async (): Promise<RevokeOutcome | undefined> => {
        if (!idsToRevoke.length || isDeleting || pending.current) return;
        pending.current = true;
        setOutcome(undefined);
        try {
            const result = await deleteServiceAccount({ serviceAccountIds: idsToRevoke });
            if ('error' in result) return;
            const successfulIds = new Set(
                result.data?.results
                    ?.filter((account) => account.status === 'SUCCESS')
                    .map((account) => account.serviceAccountId)
            );
            const failedIds = idsToRevoke.filter((id) => !successfulIds.has(id));
            const nextOutcome = { successCount: idsToRevoke.length - failedIds.length, failedIds };
            setSelectedIds((currentIds) => currentIds.filter((id) => !successfulIds.has(id)));
            setIdsToRevoke(failedIds);
            setFilteredAccounts((accounts) =>
                accounts.filter((account) => !successfulIds.has(account.serviceAccountId))
            );
            setOutcome(nextOutcome);
            notifyOutcome(nextOutcome);
            return nextOutcome;
        } finally {
            pending.current = false;
        }
    };

    return {
        serviceAccounts,
        isLoading: isLoading || isFetching,
        setQuery,
        selectedIds,
        setSelectedIds,
        idsToRevoke,
        setIdsToRevoke,
        filteredAccounts,
        isDeleting,
        deleteSuccess: outcome !== undefined && isDeleteSuccess,
        isDeleteVisible,
        createAccount,
        openDelete,
        cancelDelete,
        revoke,
        successCount: outcome?.successCount ?? 0,
        failedIds: outcome?.failedIds ?? [],
    };
};
